/**
 * Veilige toegang tot localStorage voor kleine UI-voorkeuren (winkelmand, weggeklikte meldingen).
 * Privémodus of volle opslag breekt nooit het scherm: lezen geeft dan `null`, schrijven doet niets.
 *
 * Klant- en beheerdata horen hier NIET: die lopen via de API (en in de demo via demo/db.ts).
 */
export function readLocal(key: string): string | null {
  try {
    return window.localStorage.getItem(key)
  } catch {
    return null
  }
}

/** Een lege waarde haalt de sleutel weg (zoals `writeSession`), zodat er geen lege resten blijven staan. */
export function writeLocal(key: string, value: string): void {
  try {
    if (value) window.localStorage.setItem(key, value)
    else window.localStorage.removeItem(key)
  } catch {
    /* niet erg: dan onthoudt de browser het niet */
  }
}

/** JSON lezen met terugval als het ontbreekt of beschadigd is. */
export function readLocalJson<T>(key: string, fallback: T): T {
  const raw = readLocal(key)
  if (!raw) return fallback
  try {
    return JSON.parse(raw) as T
  } catch {
    return fallback
  }
}

export function writeLocalJson(key: string, value: unknown): void {
  writeLocal(key, JSON.stringify(value))
}

/** Alle sleutels van deze app beginnen hiermee; zo raakt herstellen nooit iets van een ander. */
export const APP_KEY_PREFIX = 'bb-'

/**
 * Noodknop: wist alles wat deze app in de browser bewaart (local én session), en verder niets.
 * Voor als beschadigde opgeslagen data de app bij elke start laat vastlopen.
 * Geeft terug hoeveel sleutels er weg zijn.
 */
export function clearLocalData(): number {
  let removed = 0
  for (const store of [safeStore('localStorage'), safeStore('sessionStorage')]) {
    if (!store) continue
    try {
      const keys = Array.from({ length: store.length }, (_, i) => store.key(i)).filter(
        (k): k is string => k !== null && k.startsWith(APP_KEY_PREFIX),
      )
      for (const k of keys) store.removeItem(k)
      removed += keys.length
    } catch {
      /* niet erg: dan is er niets te wissen */
    }
  }
  return removed
}

function safeStore(name: 'localStorage' | 'sessionStorage'): Storage | null {
  try {
    return window[name]
  } catch {
    return null
  }
}

/** sessionStorage: alleen voor deze tab en deze sessie (bijv. "al één keer herladen na een deploy"). */
export function readSession(key: string): string | null {
  try {
    return window.sessionStorage.getItem(key)
  } catch {
    return null
  }
}

export function writeSession(key: string, value: string): void {
  try {
    if (value) window.sessionStorage.setItem(key, value)
    else window.sessionStorage.removeItem(key)
  } catch {
    /* niet erg */
  }
}
